import { hashKey } from '@tanstack/react-query'

import { formatLocalDate, parseLocalDate } from '@/ui/local-date'

import type { AdHocQueryLogicalType, QueryCatalogColumn, QueryCatalogTable } from '../api/query-explorer-api'

import type {
  ReportSourceRef,
  ReportFieldRef,
  ReportColumn,
  ReportOperator,
  ReportOperand,
  ReportComparison,
  ReportConditionGroup,
  ReportConditionNode,
  ReportAggregationFunction,
  ReportAggregation,
  ReportGroup,
  ReportHavingComparison,
  ReportHavingGroup,
  ReportHavingNode,
  ReportMeasureRef,
  ReportOutputColumn,
  ReportSort,
  TimeBucketUnit,
  ReportJoinType,
  ReportQueryDefinition,
} from '@/modules/report-table'

export type {
  ReportSourceRef,
  ReportSource,
  ReportFieldRef,
  ReportColumn,
  ReportOperator,
  RelativeUnit,
  ReportConditionValue,
  ReportOperand,
  ReportComparison,
  ReportConditionGroup,
  ReportConditionNode,
  ReportAggregationFunction,
  ReportAggregation,
  ReportGroup,
  ReportHavingComparison,
  ReportHavingGroup,
  ReportHavingNode,
  ReportMeasureRef,
  ReportOutputColumn,
  ReportSort,
  TimeBucketUnit,
  ReportJoinType,
  ReportJoin,
  ReportQueryDefinition,
  SqlReportQuery,
  ReportEnvelope,
} from '@/modules/report-table'
export { REPORT_DEFINITION_VERSION, structuredEnvelope, sqlEnvelope } from '@/modules/report-table'

export const MAX_JOINS = 8

export const JOIN_TYPE_LABELS: Record<ReportJoinType, string> = {
  inner: 'Inner join',
  left: 'Left join',
  right: 'Right join',
  full: 'Full join',
}

export const JOIN_TYPE_DESCRIPTIONS: Record<ReportJoinType, string> = {
  inner: 'Only rows with a match on both sides.',
  left: 'Every row from the sources above, matched rows from this one.',
  right: 'Every row from this source, matched rows from the sources above.',
  full: 'Every row from either side, matched where possible. Each condition must say a field of this source is a field from a source above.',
}

export const TIME_BUCKET_UNITS: TimeBucketUnit[] = ['hour', 'day', 'week', 'month', 'quarter', 'year']

export const TIME_BUCKET_LABELS: Record<TimeBucketUnit, string> = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year',
}

export const DEFAULT_TIME_BUCKET_UNIT: TimeBucketUnit = 'day'

const BUCKET_FORMATS: Record<TimeBucketUnit, Intl.DateTimeFormatOptions> = {
  hour: { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric' },
  day: { month: 'short', day: 'numeric', year: 'numeric' },
  week: { month: 'short', day: 'numeric', year: 'numeric' },
  month: { month: 'short', year: 'numeric' },
  quarter: { month: 'short', year: 'numeric' },
  year: { year: 'numeric' },
}

export function bucketInstant(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const ms = typeof value === 'number' ? value : Date.parse(String(value))
  return Number.isNaN(ms) ? undefined : ms
}

const SERVER_BUCKET_TIME_ZONE = 'UTC'

export function formatBucket(value: unknown, unit: TimeBucketUnit): string {
  const ms = bucketInstant(value)
  if (ms === undefined) return value === null || value === undefined ? 'null' : String(value)
  const date = new Date(ms)
  if (unit === 'quarter') return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`
  const text = new Intl.DateTimeFormat('en-US', { ...BUCKET_FORMATS[unit], timeZone: SERVER_BUCKET_TIME_ZONE }).format(date)
  return unit === 'week' ? `Week of ${text}` : text
}

export function formatTimeBucket(column: string, unit: TimeBucketUnit) {
  return (name: string, value: unknown) => (name === column ? formatBucket(value, unit) : undefined)
}

const NULL_OPERATORS: ReportOperator[] = ['isNull', 'isNotNull']

const OPERATORS_BY_TYPE: Record<AdHocQueryLogicalType, ReportOperator[]> = {
  string: ['equals', 'notEquals', 'contains', 'startsWith', 'endsWith', ...NULL_OPERATORS],
  integer: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'between', ...NULL_OPERATORS],
  float: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte', 'between', ...NULL_OPERATORS],
  boolean: ['isTrue', 'isFalse', ...NULL_OPERATORS],
  timestamp: ['lt', 'gt', 'between', 'relativeRange', ...NULL_OPERATORS],
  json: NULL_OPERATORS,
  unknown: [],
}

const FIELD_OPERATORS_BY_TYPE: Record<AdHocQueryLogicalType, ReportOperator[]> = {
  string: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte'],
  integer: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte'],
  float: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte'],
  boolean: ['equals', 'notEquals'],
  timestamp: ['equals', 'notEquals', 'lt', 'lte', 'gt', 'gte'],
  json: [],
  unknown: [],
}

export const OPERATOR_LABELS: Record<ReportOperator, string> = {
  equals: 'is',
  notEquals: 'is not',
  contains: 'contains',
  startsWith: 'starts with',
  endsWith: 'ends with',
  lt: 'is less than',
  lte: 'is at most',
  gt: 'is greater than',
  gte: 'is at least',
  between: 'is between',
  isTrue: 'is yes',
  isFalse: 'is no',
  relativeRange: 'is within the last',
  isNull: 'is empty',
  isNotNull: 'is not empty',
}

const DATE_OPERATOR_LABELS: Partial<Record<ReportOperator, string>> = {
  lt: 'is before',
  lte: 'is on or before',
  gt: 'is after',
  gte: 'is on or after',
}

export function operatorLabel(operator: ReportOperator, type: AdHocQueryLogicalType | undefined): string {
  return (type === 'timestamp' ? DATE_OPERATOR_LABELS[operator] : undefined) ?? OPERATOR_LABELS[operator]
}

export const AGGREGATION_LABELS: Record<ReportAggregationFunction, string> = {
  rowCount: 'Number of rows',
  count: 'Count of',
  sum: 'Sum of',
  avg: 'Average of',
  min: 'Minimum of',
  max: 'Maximum of',
}

const NUMERIC_TYPES: AdHocQueryLogicalType[] = ['integer', 'float']
const GROUPABLE_TYPES: AdHocQueryLogicalType[] = ['string', 'boolean', 'integer', 'timestamp']
const MAX_ALIAS_BYTES = 63

export function operatorsFor(type: AdHocQueryLogicalType): ReportOperator[] {
  return OPERATORS_BY_TYPE[type] ?? []
}

export function fieldOperatorsFor(type: AdHocQueryLogicalType): ReportOperator[] {
  return FIELD_OPERATORS_BY_TYPE[type] ?? []
}

export function operandShape(operator: ReportOperator, type: AdHocQueryLogicalType): 'none' | 'text' | 'number' | 'range' | 'date' | 'dateRange' | 'relative' {
  if (NULL_OPERATORS.includes(operator) || operator === 'isTrue' || operator === 'isFalse') return 'none'
  if (operator === 'relativeRange') return 'relative'
  if (operator === 'between') return type === 'timestamp' ? 'dateRange' : 'range'
  if (type === 'timestamp') return 'date'
  return NUMERIC_TYPES.includes(type) ? 'number' : 'text'
}

export function aggregationsFor(type: AdHocQueryLogicalType): ReportAggregationFunction[] {
  const functions: ReportAggregationFunction[] = ['count']
  if (NUMERIC_TYPES.includes(type)) functions.push('sum', 'avg')
  if (NUMERIC_TYPES.includes(type) || type === 'timestamp') functions.push('min', 'max')
  return functions
}

export function measureType(measure: ReportMeasureRef, fieldType: AdHocQueryLogicalType | undefined): AdHocQueryLogicalType {
  if (measure.function === 'rowCount' || measure.function === 'count') return 'integer'
  if (measure.function === 'avg') return 'float'
  return fieldType ?? 'unknown'
}

export function havingOperatorsFor(type: AdHocQueryLogicalType): ReportOperator[] {
  if (NUMERIC_TYPES.includes(type)) return ['gt', 'gte', 'lt', 'lte', 'equals', 'notEquals', 'between']
  return type === 'timestamp' ? ['gt', 'gte', 'lt', 'lte', 'between'] : []
}

export function isHavingComparison(node: ReportHavingNode): node is ReportHavingComparison {
  return 'left' in node
}

export function havingConditions(group: ReportHavingGroup | undefined): ReportHavingNode[] {
  if (!group) return []
  return 'and' in group ? group.and : group.or
}

function havingWithout(group: ReportHavingGroup | undefined, alias: string): ReportHavingGroup | undefined {
  if (!group) return undefined
  const kept = havingConditions(group).filter((node) => !isHavingComparison(node) || node.left.field?.source !== alias)
  return 'or' in group ? { or: kept } : { and: kept }
}

export function canGroupBy(type: AdHocQueryLogicalType): boolean {
  return GROUPABLE_TYPES.includes(type)
}

export function groupsWithoutBucket(type: AdHocQueryLogicalType | undefined): boolean {
  return type !== undefined && type !== 'timestamp' && canGroupBy(type)
}

export function isComparison(node: ReportConditionNode): node is ReportComparison {
  return 'left' in node
}

export function isFieldOperand(operand: ReportOperand | undefined): operand is ReportFieldRef {
  return operand !== undefined && 'column' in operand
}

export function groupConditions(group: ReportConditionGroup | undefined): ReportConditionNode[] {
  if (!group) return []
  return 'and' in group ? group.and : group.or
}

export function groupCombinator(group: ReportConditionGroup | undefined): 'and' | 'or' {
  return group && 'or' in group ? 'or' : 'and'
}

export function conditionGroup(combinator: 'and' | 'or', conditions: ReportConditionNode[]): ReportConditionGroup {
  return combinator === 'or' ? { or: conditions } : { and: conditions }
}

export function fieldKey(field: ReportFieldRef): string {
  return `${field.source}.${field.column}`
}

export function sameField(left: ReportFieldRef | undefined, right: ReportFieldRef | undefined): boolean {
  return left !== undefined && right !== undefined && left.source === right.source && left.column === right.column
}

export function isMeasure(column: ReportOutputColumn): column is ReportAggregation {
  return 'function' in column
}

export function isFieldColumn(column: ReportOutputColumn): column is ReportColumn {
  return !isMeasure(column)
}

export function columnOutputName(column: ReportOutputColumn): string {
  return isMeasure(column) ? aggregationAlias(column) : column.as ?? column.column
}

export function aggregationAlias(aggregation: ReportAggregation): string {
  if (aggregation.as) return aggregation.as
  return aggregation.function === 'rowCount' ? 'row_count' : `${aggregation.function}_${aggregation.field?.column ?? ''}`
}

export function isAggregated(definition: ReportQueryDefinition): boolean {
  return definition.columns.some(isMeasure) || (definition.groupBy?.length ?? 0) > 0
}

export function groupOf(groupBy: ReportGroup[] | undefined, field: ReportFieldRef): ReportGroup | undefined {
  return groupBy?.find((group) => sameField(group, field))
}

export function outputColumns(definition: ReportQueryDefinition): string[] {
  return definition.columns.map(columnOutputName)
}

function outputIdentities(definition: ReportQueryDefinition): string[] {
  return definition.columns.map((column) => isMeasure(column)
    ? `${column.function}:${column.field ? fieldKey(column.field) : ''}`
    : fieldKey(column))
}



export function sortPositions(definition: ReportQueryDefinition, remembered: (number | undefined)[] = []): number[] {
  const names = outputColumns(definition)
  const claimed = new Set(remembered.filter((position): position is number => position !== undefined))
  return (definition.sort ?? []).map((entry, sortIndex) => {
    const kept = remembered[sortIndex]
    if (kept !== undefined) return kept
    const position = names.findIndex((name, index) => name === entry.field && !claimed.has(index))
    claimed.add(position)
    return position
  })
}

export function followRenames(
  before: ReportQueryDefinition,
  after: ReportQueryDefinition,
  positions = sortPositions({ ...before, sort: after.sort }),
): { sort: ReportSort[]; positions: number[] } | undefined {
  const identities = outputIdentities(before)
  const next = outputIdentities(after)
  if (identities.length !== next.length || identities.some((identity, index) => identity !== next[index])) return undefined
  const names = outputColumns(after)
  const kept = (after.sort ?? []).flatMap((entry, index) => {
    const position = positions[index] ?? -1
    const field = names[position]
    return field === undefined ? [] : [{ entry: { ...entry, field }, position }]
  })
  return { sort: kept.map(({ entry }) => entry), positions: kept.map(({ position }) => position) }
}

export function aliasProblem(alias: string): string | undefined {
  if (alias === '') return undefined
  if (alias.trim() !== alias) return 'Remove the spaces at the start or end.'
  if (/\p{Cc}/u.test(alias)) return 'Remove tabs and other invisible characters.'
  if (/^_key_/i.test(alias)) return 'Names starting with _key_ are reserved.'
  if (new TextEncoder().encode(alias).length > MAX_ALIAS_BYTES) return 'That name is too long.'
  return undefined
}

export function duplicateOutputNames(definition: ReportQueryDefinition): string[] {
  const seen = new Set<string>()
  const duplicates = new Set<string>()
  for (const name of outputColumns(definition)) {
    const key = name.toLowerCase()
    if (seen.has(key)) duplicates.add(name)
    else seen.add(key)
  }
  return [...duplicates]
}

export function definitionProblem(definition: ReportQueryDefinition): string | undefined {
  const aliases = definition.columns
    .map((entry) => entry.as)
    .filter((alias): alias is string => alias !== undefined)
  if (aliases.some((alias) => aliasProblem(alias) !== undefined)) return 'Fix the column names marked in the editor before running.'
  const duplicate = duplicateOutputNames(definition)[0]
  return duplicate ? `Two columns are named ${duplicate}. Rename one of them.` : undefined
}

export type ReportSourceEntry = { alias: string; table: QueryCatalogTable | undefined }

export function reportSources(definition: ReportQueryDefinition, tables: QueryCatalogTable[]): ReportSourceEntry[] {
  return [definition.from, ...(definition.joins ?? []).map((join) => join.source)]
    .map((source) => ({ alias: source.alias, table: findTable(tables, source) }))
}

export function sourceLabel(sources: ReportSourceEntry[], alias: string): string {
  const entry = sources.find((candidate) => candidate.alias === alias)
  const name = entry?.table?.displayName ?? alias
  const repeated = sources.filter((candidate) => candidate.table !== undefined && candidate.table === entry?.table).length > 1
  return repeated ? `${name} as ${alias}` : name
}

export function fieldLabel(sources: ReportSourceEntry[], field: ReportFieldRef): string {
  const name = fieldDisplayName(sources, field)
  return sources.length > 1 ? `${name} (${sourceLabel(sources, field.source)})` : name
}

function fieldDisplayName(sources: ReportSourceEntry[], field: ReportFieldRef): string {
  return findColumn(sources.find((candidate) => candidate.alias === field.source)?.table, field.column)?.displayName ?? field.column
}

export type SelectableField = { key: string; ref: ReportFieldRef; label: string; column: QueryCatalogColumn }

export function selectableFields(sources: ReportSourceEntry[]): SelectableField[] {
  return sources.flatMap((source) => (source.table?.columns ?? []).map((column) => {
    const ref = { source: source.alias, column: column.name }
    return { key: fieldKey(ref), ref, label: fieldLabel(sources, ref), column }
  }))
}

export function findField(fields: SelectableField[], ref: ReportFieldRef | undefined): SelectableField | undefined {
  return ref === undefined ? undefined : fields.find((field) => field.key === fieldKey(ref))
}

export function outputColumnLabel(sources: ReportSourceEntry[], definition: ReportQueryDefinition, name: string): string {
  const column = definition.columns.find((candidate) => columnOutputName(candidate) === name)
  return column ? columnLabel(sources, column, definition.groupBy) : name
}

export function columnLabel(sources: ReportSourceEntry[], column: ReportOutputColumn, groupBy?: ReportGroup[]): string {
  if (column.as) return column.as
  if (isMeasure(column)) {
    if (column.function === 'rowCount' || !column.field) return AGGREGATION_LABELS.rowCount
    return `${AGGREGATION_LABELS[column.function]} ${fieldDisplayName(sources, column.field)}`
  }
  const label = fieldDisplayName(sources, column)
  const bucket = groupOf(groupBy, column)?.bucket
  return bucket ? `${label} by ${TIME_BUCKET_LABELS[bucket].toLowerCase()}` : label
}

export function outputColumnLabels(
  sources: ReportSourceEntry[],
  definition: ReportQueryDefinition,
  columns: { name: string }[],
): Record<string, string> {
  return Object.fromEntries(columns.map((column) => [column.name, outputColumnLabel(sources, definition, column.name)]))
}

export function bucketedColumn(definition: ReportQueryDefinition): { name: string; unit: TimeBucketUnit } | undefined {
  const group = definition.groupBy?.find((candidate) => candidate.bucket !== undefined)
  const column = group ? definition.columns.filter(isFieldColumn).find((candidate) => sameField(candidate, group)) : undefined
  return column && group?.bucket ? { name: columnOutputName(column), unit: group.bucket } : undefined
}

export function sourceKey(source: ReportSourceRef): string {
  return `${source.catalog}.${source.schema}.${source.table}`
}

export function tableKey(table: QueryCatalogTable): string {
  return `${table.catalog}.${table.schema}.${table.name}`
}

export function tableToSource(table: QueryCatalogTable): ReportSourceRef {
  return { catalog: table.catalog, schema: table.schema, table: table.name }
}

export function findTable(tables: QueryCatalogTable[], source: ReportSourceRef | undefined): QueryCatalogTable | undefined {
  return source === undefined ? undefined : tables.find((table) => tableKey(table) === sourceKey(source))
}

export function findColumn(table: QueryCatalogTable | undefined, name: string): QueryCatalogColumn | undefined {
  return table?.columns.find((column) => column.name === name)
}

export function nextSourceAlias(definition: ReportQueryDefinition, table: QueryCatalogTable): string {
  const taken = new Set([definition.from.alias, ...(definition.joins ?? []).map((join) => join.source.alias)])
  const base = baseAlias(table)
  if (!taken.has(base)) return base
  let index = 2
  while (taken.has(`${base}_${index}`)) index++
  return `${base}_${index}`
}

export function suggestJoinCondition(sources: ReportSourceEntry[], table: QueryCatalogTable, alias: string): ReportComparison | undefined {
  for (const source of sources) {
    if (!source.table) continue
    const outgoing = source.table.foreignKeys.find((fk) => fk.referencesCatalog === table.catalog && fk.referencesSchema === table.schema && fk.referencesTable === table.name)
    if (outgoing) return { left: { source: source.alias, column: outgoing.column }, op: 'equals', right: { source: alias, column: outgoing.referencesColumn } }
    const incoming = table.foreignKeys.find((fk) => fk.referencesCatalog === source.table?.catalog && fk.referencesSchema === source.table.schema && fk.referencesTable === source.table.name)
    if (incoming) return { left: { source: source.alias, column: incoming.referencesColumn }, op: 'equals', right: { source: alias, column: incoming.column } }
  }
  for (const source of sources)
    for (const column of source.table?.columns ?? []) {
      const match = table.columns.find((candidate) => candidate.type === column.type)
      if (match) return { left: { source: source.alias, column: column.name }, op: 'equals', right: { source: alias, column: match.name } }
    }
  return undefined
}

export function linksAcrossJoin(left: ReportFieldRef, right: ReportFieldRef, joinedAlias: string): boolean {
  return (left.source === joinedAlias) !== (right.source === joinedAlias)
}

export function equiJoinCondition(on: ReportConditionGroup | undefined, joinedAlias: string, fallback: ReportComparison | undefined): ReportConditionGroup {
  const kept = groupConditions(on).filter(isComparison).flatMap((condition): ReportComparison[] =>
    isFieldOperand(condition.right) && linksAcrossJoin(condition.left, condition.right, joinedAlias) ? [{ ...condition, op: 'equals' }] : [])
  return { and: kept.length > 0 ? kept : fallback ? [fallback] : [] }
}

function referencesSource(node: ReportConditionNode, alias: string): boolean {
  if (!isComparison(node)) return groupConditions(node).some((child) => referencesSource(child, alias))
  return node.left.source === alias || (isFieldOperand(node.right) && node.right.source === alias)
}

function withoutSource(group: ReportConditionGroup | undefined, alias: string): ReportConditionGroup | undefined {
  if (!group) return undefined
  return conditionGroup(groupCombinator(group), groupConditions(group).filter((node) => !referencesSource(node, alias)))
}

export function removeJoin(definition: ReportQueryDefinition, alias: string): ReportQueryDefinition {
  return {
    ...definition,
    joins: (definition.joins ?? [])
      .filter((join) => join.source.alias !== alias)
      .map((join) => ({ ...join, on: withoutSource(join.on, alias) ?? join.on })),
    filter: withoutSource(definition.filter, alias),
    columns: definition.columns.filter((column) => (isMeasure(column) ? column.field?.source : column.source) !== alias),
    groupBy: definition.groupBy?.filter((group) => group.source !== alias),
    having: havingWithout(definition.having, alias),
  }
}

export function emptyDefinition(table: QueryCatalogTable): ReportQueryDefinition {
  return { from: { alias: baseAlias(table), ...tableToSource(table) }, columns: [], sort: [] }
}

function baseAlias(table: QueryCatalogTable): string {
  return /^[a-z_][a-z0-9_]*$/.test(table.name) ? table.name : 'source'
}

function comparable(definition: ReportQueryDefinition): ReportQueryDefinition {
  return {
    ...definition,
    joins: definition.joins?.length ? definition.joins : undefined,
    filter: groupConditions(definition.filter).length ? definition.filter : undefined,
    having: havingConditions(definition.having).length ? definition.having : undefined,
    sort: definition.sort?.length ? definition.sort : undefined,
  }
}

export function sameDefinition(left: ReportQueryDefinition, right: ReportQueryDefinition): boolean {
  return hashKey([comparable(left)]) === hashKey([comparable(right)])
}

export function definitionIsRunnable(definition: ReportQueryDefinition): boolean {
  return definition.columns.length > 0
}

export function localDayOf(value: string | number | undefined): string | undefined {
  if (value === undefined || value === '') return undefined
  const instant = new Date(String(value))
  return Number.isNaN(instant.getTime()) ? undefined : formatLocalDate(instant)
}

export function startOfLocalDay(day: string | undefined): string {
  const date = parseLocalDate(day)
  return date ? date.toISOString() : ''
}

export function endOfLocalDay(day: string | undefined): string {
  const date = parseLocalDate(day)
  if (!date) return ''
  date.setDate(date.getDate() + 1)
  return new Date(date.getTime() - 1).toISOString()
}

export function dateFilterBound(operator: ReportOperator, day: string | undefined): string {
  return operator === 'gt' || operator === 'lte' ? endOfLocalDay(day) : startOfLocalDay(day)
}
